<template>
  <div :class="$attrs.class" :style="[rootStyle, $attrs.style as any]">
    <!-- prev button (both mode) -->
    <StatisticPagination
      v-if="pagination && paginationPlacement === 'both' && (canPrev || canNext)"
      :can-prev="canPrev"
      :can-next="canNext"
      mode="prev"
      :size="mergedSize"
      @prev="goPrev"
      @next="goNext"
    />

    <!-- paginated content -->
    <div v-if="pagination" ref="viewportRef" :style="{ flex: 1, minWidth: 0, overflow: 'hidden' }">
      <div
        data-statistic-track="true"
        :style="{
          display: 'flex',
          minWidth: 0,
          transform: 'translateX(-' + (pageIndex * viewportWidth) + 'px)',
          transition: 'transform 300ms ease',
        }"
      >
        <div
          v-for="(pageEntries, page) in pages"
          :key="page"
          data-statistic-page="true"
          :data-statistic-page-active="page === pageIndex ? 'true' : 'false'"
          :aria-hidden="page !== pageIndex"
          :style="pageStyle(page)"
        >
          <div :style="{ display: 'flex', alignItems: mergedEqualHeight ? 'stretch' : 'flex-start', gap: '4px', width: '100%' }">
            <template v-for="(entry, idx) in pageEntries" :key="entry.item.id">
              <BaseStatisticCard
                :item="resolveCardItem(entry)"
                :size="mergedSize"
                :reverse-color="mergedReverseColor"
                :border="mergedBorder"
                :checked="selection.isSelected(entry.item.id)"
                :control-type="selectionControlType"
                :equal-height="mergedEqualHeight"
                :number-scroll="mergedNumberScroll"
                :sub-indicators-layout="mergedSubIndicatorsLayout"
                :style="cardStyle"
                @click="selectionControlType ? selection.toggle($event.id) : undefined"
              />
              <FormulaOperator
                v-if="idx < pageEntries.length - 1"
                :operator="entry.operator"
                :size="mergedSize"
              />
            </template>
          </div>
        </div>
      </div>
    </div>

    <!-- non-paginated content -->
    <JdScrollbar v-else :style="{ flex: 1, minWidth: 0 }">
      <div :style="{ display: 'flex', alignItems: mergedEqualHeight ? 'stretch' : 'flex-start', gap: '4px', width: '100%', minWidth: 0 }">
        <template v-for="(entry, idx) in data" :key="entry.item.id">
          <BaseStatisticCard
            :item="resolveCardItem(entry)"
            :size="mergedSize"
            :reverse-color="mergedReverseColor"
            :border="mergedBorder"
            :checked="selection.isSelected(entry.item.id)"
            :control-type="selectionControlType"
            :equal-height="mergedEqualHeight"
            :number-scroll="mergedNumberScroll"
            :sub-indicators-layout="mergedSubIndicatorsLayout"
            :style="cardStyle"
            @click="selectionControlType ? selection.toggle($event.id) : undefined"
          />
          <FormulaOperator
            v-if="idx < data.length - 1"
            :operator="entry.operator"
            :size="mergedSize"
          />
        </template>
      </div>
    </JdScrollbar>

    <!-- next / stacked button -->
    <div v-if="pagination && (canPrev || canNext)" :style="{ display: 'flex' }">
      <StatisticPagination
        :can-prev="canPrev"
        :can-next="canNext"
        :mode="paginationPlacement === 'single' ? 'stacked' : 'next'"
        :size="mergedSize"
        @prev="goPrev"
        @next="goNext"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
// @ts-nocheck SFC 顶层复杂联合 prop(value: string|number|Array)在 dts 生成器的 SFC 运行时编译中会被降级为 `type:[null,Array]`，null 非构造函数触发 TS2769;源码类型已由 typecheck:web 保证,此处沿用 dts 生成器内置的 @ts-nocheck 逃逸
import { computed, inject, provide, reactive, ref, watch, type CSSProperties } from 'vue'
import JdScrollbar from '@jdesign-vue/components/scrollbar'
import { statisticCardDataKey, statisticCardAppearanceKey } from '../tokens'
import { CARD_GAP, CARD_MIN_WIDTH } from '../constants'
import { useElementWidth } from '../composables/use-element-width'
import { useSelection } from '../composables/use-selection'
import { buildPageStarts } from '../composables/use-pagination'
import BaseStatisticCard from '../base/statistic-card.vue'
import StatisticPagination from '../supporting/statistic-pagination.vue'
import FormulaOperator from '../supporting/formula-operator.vue'
import type {
  StatisticCardSize,
  PaginationConfig,
  StatisticCardItemData,
  StatisticCardSelectionValue,
  SelectionProps,
} from '../types'

type FormulaEntry = { item: StatisticCardItemData; operator?: string }

defineOptions({ name: 'JdStatisticCardFormula', inheritAttrs: false })

const props = withDefaults(
  defineProps<{
    data: FormulaEntry[]
    size?: StatisticCardSize
    reverseColor?: boolean
    border?: boolean
    equalHeight?: boolean
    numberScroll?: boolean
    pagination?: PaginationConfig
    cardWidth?: number | 'auto'
    minCardWidth?: number
    subIndicatorsLayout?: 'horizontal' | 'vertical'
    controlType?: 'radio' | 'checkbox' | null
    value?: StatisticCardSelectionValue | StatisticCardSelectionValue[]
    maxLength?: number
    minLength?: number
  }>(),
  {
    // Vue Boolean 类型 prop 未传入时会被隐式转为 false,
    // 显式声明 undefined 才能让下游 `?? true` 的默认值链生效(对齐 React formula border=true)
    border: undefined,
    reverseColor: undefined,
    equalHeight: undefined,
    numberScroll: undefined,
  },
)

const emit = defineEmits<{
  (e: 'change', value: StatisticCardSelectionValue | StatisticCardSelectionValue[]): void
  (e: 'update:value', value: StatisticCardSelectionValue | StatisticCardSelectionValue[]): void
}>()

// controlType 为可选 'radio' | 'checkbox' | null，直接传 props 无法匹配 SelectionProps 判别联合，
// 这里显式收敛为联合分支(对齐 super-group 的做法)，并用 reactive 代理保持响应性。
const selectionProps = reactive({
  controlType: computed(() => (props.controlType === 'radio' || props.controlType === 'checkbox' ? props.controlType : null)),
  value: computed(() => props.value),
  maxLength: computed(() => props.maxLength),
  minLength: computed(() => props.minLength),
  onChange: (value: StatisticCardSelectionValue | StatisticCardSelectionValue[]) => emit('change', value),
  'onUpdate:value': (value: StatisticCardSelectionValue | StatisticCardSelectionValue[]) => emit('update:value', value),
}) as unknown as SelectionProps

const selection = useSelection(selectionProps)
// 顶层 ref 才会在模板中自动解包，避免把 ComputedRef 对象作为 controlType 传下去
const selectionControlType = selection.controlType

// inject 容器（Group / SuperGroup）注入的外观上下文，未显式传入时回退继承
const appearanceCtx = inject(statisticCardAppearanceKey, undefined)
const appearance = computed(() => appearanceCtx?.appearance.value)
const mergedSize = computed<StatisticCardSize>(() => props.size ?? appearance.value?.size ?? 'large')
const mergedReverseColor = computed(() => props.reverseColor ?? appearance.value?.reverseColor ?? false)
const mergedBorder = computed(() => props.border ?? appearance.value?.border ?? true)
const mergedEqualHeight = computed(() => props.equalHeight ?? appearance.value?.equalHeight ?? true)
const mergedNumberScroll = computed(() => props.numberScroll ?? appearance.value?.numberScroll)
const mergedSubIndicatorsLayout = computed(() => props.subIndicatorsLayout ?? appearance.value?.subIndicatorsLayout)

const operatorWidth = computed(() => (mergedSize.value === 'large' ? 24 : 20))
const resolvedMinWidth = computed(() => props.minCardWidth ?? (typeof props.cardWidth === 'number' ? props.cardWidth : CARD_MIN_WIDTH[mergedSize.value]))
const paginationPlacement = computed(() => props.pagination?.buttonPlacement ?? 'both')

// useElementWidth 内部自建 elRef 并返回，模板须用它返回的 ref 才能被 ResizeObserver 观察；
// 此前传参并自建 viewportRef 导致观察对象与模板绑定不一致，viewportWidth 恒 0、分页失效。
const { elRef: viewportRef, width: viewportWidth } = useElementWidth<HTMLDivElement>()

// ── resolve formula page size ──
function resolveFormulaPageSize(
  pagination: PaginationConfig | undefined,
  dataLength: number,
  containerWidth: number,
  minCardW: number,
  opWidth: number,
) {
  if (!pagination) return dataLength
  const requested = pagination.pageSize ?? 'auto'
  if (requested !== 'auto') return Math.min(Math.max(requested, 1), dataLength)
  if (dataLength <= 0) return 0
  const available = containerWidth > 0 ? containerWidth : minCardW
  let count = 1
  while (count < dataLength) {
    const next = count + 1
    const nextW = next * minCardW + count * opWidth + count * 8
    if (nextW > available) break
    count = next
  }
  return count
}

const pageSize = computed(() =>
  resolveFormulaPageSize(props.pagination, props.data.length, viewportWidth.value, resolvedMinWidth.value, operatorWidth.value),
)

const step = computed(() => props.pagination?.step ?? 'auto')
const pageStarts = computed(() => buildPageStarts(props.data.length, pageSize.value, step.value))
// 预计算每页的条目切片，避免在 v-for 模板内重复调用 data.slice(start, start + pageSize)
const pages = computed(() => pageStarts.value.map(start => props.data.slice(start, start + pageSize.value)))
const pageIndex = ref(0)
const canPrev = computed(() => pageIndex.value > 0)
const canNext = computed(() => pageIndex.value < pageStarts.value.length - 1)

watch(
  () => pageStarts.value.length,
  len => {
    if (pageIndex.value > len - 1) pageIndex.value = 0
  },
)

function goPrev() {
  pageIndex.value = Math.max(0, pageIndex.value - 1)
}
function goNext() {
  pageIndex.value = Math.min(pageIndex.value + 1, pageStarts.value.length - 1)
}

const hasExplicitWidth = computed(() => props.cardWidth != null && props.cardWidth !== 'auto')
const cardStyle = computed(() => ({
  width: hasExplicitWidth.value ? `${props.cardWidth}px` : 'auto',
  minWidth: `${resolvedMinWidth.value}px`,
  flex: !hasExplicitWidth.value ? 1 : undefined,
  alignSelf: mergedEqualHeight.value ? 'stretch' : undefined,
}))

const rootStyle = computed(() => ({
  display: 'flex',
  alignItems: 'stretch',
  gap: `${CARD_GAP}px`,
  width: '100%',
}))

// 显式声明 CSSProperties 返回类型，避免模板内 pointerEvents 三元结果被推断为宽 string 触发 TS2322
function pageStyle(page: number): CSSProperties {
  return { flex: '0 0 100%', minWidth: '0', pointerEvents: page === pageIndex.value ? 'auto' : 'none' }
}

function resolveCardItem(entry: FormulaEntry) {
  const disabled = entry.item.disabled || selection.isSelectionDisabled(entry.item.id)
  return { ...entry.item, disabled }
}

// provide card data context
const statisticCardData = computed(() => props.data.map(e => e.item))
provide(statisticCardDataKey, { data: statisticCardData })
</script>
